import { HelpersService } from './../../helpers.service';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { from, Observable, of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { ProductsApiService } from 'src/app/builder-services/api/products.api.service'
import { IntegrationsService } from 'src/app/integrations/integrations.service';
import { environment } from 'src/environments/environment'
export interface productsTableColumns {
  Id: string
  imageUrl: string[]
  productName: string
  productSKU: string
  productStock: string
  regularPrice: string
  salePrice?: string
  date: string
  checked?: boolean
}
@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  constructor(
    private builderService: BuilderService,
    private productsApi: ProductsApiService,
    private http: HttpClient,
    private integrationsService: IntegrationsService,
    private helpersService: HelpersService
  ) {
    this.syncProductsCount().subscribe((count) => {
      this.totalProductsCount = count || 0
      this.paginationCount = count || 0
    })
  }


  /**
   * Updates a product, creating it when no matching row exists.
   *
   * The upsert fallback came from the old data-layer facade. It belongs here
   * rather than in ProductsApiService, which stays an honest mirror of the
   * backend endpoints.
   */
  private async updateProductWithFallback(projectId: string, productId: string, product: any): Promise<void> {
    try {
      const found = await this.productsApi.updateProduct(projectId, productId, product)
      // Row didn't exist — create it through the creation layer.
      if (!found) await this.productsApi.insertProduct(projectId, { ...product, productId: [productId] })
    } catch (err) {
      console.warn('[ProductsService] updateProduct failed:', err)
    }
  }

  storedStandaloneProducts: any
  get project_id() {
    return this.builderService.selectedProject
  }
  storedVendorProducts: any
  totalProductsCount: number = 0   // always the grand total — used for the limit counter
  paginationCount: number = 0      // count for the active filter — used for pagination math
  // Map-based cache to prevent duplicates (keys are inherently unique)
  private productsCache: Map<string, any> = new Map()
  allProductsLoaded = false
  lastProductKey: string | null = null
  fetchingInProgress: boolean = false;
  pageIndex: number = 0;
  pageSize: number = 5;

  /**
   * Adds products to cache. Map prevents duplicates by key.
   */
  addProductsToCache(products: any[]): void {
    products.forEach((product) => {
      const productId = String(product.Id || '')
      if (productId && !this.productsCache.has(productId)) {
        this.productsCache.set(productId, product)
      }
    })
  }

  /**
   * Returns all cached products as an array
   */
  get allProducts(): any[] {
    return Array.from(this.productsCache.values())
  }

  /**
   * Clears the products cache
   */
  clearProductsCache(): void {
    this.productsCache.clear()
  }

  /**
   * Updates a product in the cache
   */
  updateProductInCache(productId: string, productData: any): void {
    if (this.productsCache.has(productId)) {
      const existing = this.productsCache.get(productId)
      this.productsCache.set(productId, { ...existing, ...productData })
    }
  }
  options = {
    headers: new HttpHeaders().set('Content-Type', 'application/json')
  }
  submitProduct = (product: any, product_id: string = '') => {
    product = JSON.parse(JSON.stringify(product))
    return new Observable<any>((observer) => {
      if (!product_id) {
        this.productsApi.insertProduct(this.project_id, product).then((supabaseUUID) => {
          if (!supabaseUUID) { observer.error(new Error('Failed to insert product')); return }
          product.id = supabaseUUID
          this.totalProductsCount++
          this.syncProductWP(product, supabaseUUID).subscribe()
          observer.next({ id: supabaseUUID })
          observer.complete()
        }).catch((error) => observer.error(error))
      } else {
        this.updateProductWithFallback(this.project_id, product_id, product).then(() => {
          this.updateProductInCache(product_id, product)
          const productForSync = this.productsCache.get(product_id) ?? product
          this.syncProductWP(productForSync, product_id).subscribe()
          observer.next({ id: product_id })
          observer.complete()
        }).catch((error) => observer.error(error))
      }
    })
  }

  fetchPage(page = 0, pageSize = this.pageSize, searchText?: string, sortBy?: string): Observable<any[]> {
    return from(this.productsApi.getProducts(this.project_id, page, pageSize, searchText, sortBy)).pipe(
      tap(products => {
        this.addProductsToCache(products)
        this.migrateProductImages(products)
      })
    )
  }

  fetchPageByCategory(categoryId: string, page = 0, pageSize = this.pageSize, searchText?: string, sortBy?: string): Observable<any[]> {
    return from(this.productsApi.getProductsByCategory(this.project_id, categoryId, page, pageSize, searchText, sortBy)).pipe(
      tap(products => this.addProductsToCache(products))
    )
  }

  getProductsCountByCategory(categoryId: string, searchText?: string): Observable<number> {
    return from(this.productsApi.getProductsCountByCategory(this.project_id, categoryId, searchText))
  }

  syncProductsCount(searchText?: string): Observable<number> {
    return from(this.productsApi.getProductsCount(this.project_id, searchText))
  }

  syncProductWP = (product: any, product_id: string = '') => {
    return this.integrationsService.getWordpressSiteUrl().pipe(
      switchMap((url: string) => {
        if (!url) return of(null)
        const params = new URLSearchParams()
        for (const key in product) {
          if (typeof product[key] === 'object') {
            params.append(key, JSON.stringify(product[key]))
          } else {
            params.append(key, product[key])
          }
        }
        params.append('product_id', product_id)
        if (product.source === 'wordpress' && product.external_id) {
          params.append('wp_product_id', product.external_id)
        }
        const wordPressURL = environment.proxyUrl + url + '/wp-json/v4/syncAddProduct'
        const urlWithParams = `${wordPressURL}?${encodeURIComponent(params.toString())}`
        return this.http.get(urlWithParams)
      })
    )
  }

  syncAllProductsWP = () => {
    return this.integrationsService.getWordpressSiteUrl().pipe(
      switchMap((url: string) => {
        if (!url) return of(null)
        const wordpressURL = environment.proxyUrl + url + '/wp-json/v4/syncAllProducts'
        return this.http.get(wordpressURL)
      })
    )
  }
  /**
   * Retrieves products with support for pagination, full loading, and in-memory caching.
   *
   * @param mode - Determines how products are resolved.
   *  - 'paginated' (default): Fetches products incrementally using a pagination cursor.
   *  - 'all': Fetches and caches the entire product collection in a single request
   *    (used for search, filtering, and bulk operations).
   *
   * @param options - Optional configuration for paginated loading.
   * @param options.limit - Maximum number of products to fetch per request (default: 20).
   * @param options.lastKey - Pagination cursor returned from the previous request.
   *
   * @returns {Observable<any>}
   *  - In paginated mode, emits an object containing:
   *      - `items`: the newly fetched products
   *      - `lastKey`: the next pagination cursor
   *  - In all mode, emits the full cached product list.
   *
   * @remarks
   * This method is the single source of truth for product loading.
   *
   * It internally uses `useCachedProducts` to immediately resolve data from
   * memory when all products are already loaded, avoiding unnecessary
   * network requests.
   *
   * The service manages request concurrency, pagination state, and cache
   * validity internally. Components must not mutate loading or pagination
   * flags directly.
   */

  /** Every product for the project, unpaginated. */
  getAllProducts = (): Promise<any[]> => {
    return this.productsApi.getAllProducts(this.project_id)
  }

  getAllProductsForExport = (): Observable<any[]> => {
    return from(this.productsApi.getAllProductsForExport(this.project_id))
  }

  getProducts = (mode: 'all' | 'paginated' = 'paginated', options?: { limit?: number; lastKey?: string | null }) => {
    const limit = options?.limit ?? 20
    const lastKey = options?.lastKey ?? undefined
    return new Observable<any>((observer) => {
      if (this.useCachedProducts(observer)) return
      if (this.fetchingInProgress) return

      this.fetchingInProgress = true
      const path = `projects/${this.project_id}/products`

      if (mode === 'paginated') {
        this.fetchNextProductsPage(path, limit, lastKey, observer)
      } else {
        this.fetchAndCacheAllProducts(path, observer)
      }
    })
  }
  private useCachedProducts(observer: any): boolean {
    if (!this.allProductsLoaded) return false
    observer.next(this.allProducts)
    observer.complete()
    return true
  }
  private fetchNextProductsPage(path: string, limit: number, lastKey: string | undefined, observer: any) {
    const page = lastKey ? parseInt(lastKey, 10) || 0 : 0
    from(this.productsApi.getProducts(this.project_id, page, limit, undefined)).subscribe(
      (items) => {
        this.fetchingInProgress = false

        if (!items || !items.length) {
          this.allProductsLoaded = true
          observer.next({ items: [], lastKey: null })
          observer.complete()
          return
        }
        // Add to cache (Map prevents duplicates automatically)
        this.addProductsToCache(items)
        const nextPage = page + 1
        this.lastProductKey = String(nextPage)
        const countIsKnown = this.totalProductsCount > 0
        const hasLoadedAll = countIsKnown && this.allProducts.length >= this.totalProductsCount
        const noMorePages = items.length < limit && countIsKnown
        this.allProductsLoaded = hasLoadedAll || noMorePages
        observer.next({
          items,
          lastKey: this.allProductsLoaded ? null : String(nextPage)
        })
        observer.complete()
      },
      (error) => {
        this.fetchingInProgress = false
        observer.error(error)
      }
    )
  }
  private fetchAndCacheAllProducts(path: string, observer: any) {
    from(this.productsApi.getProducts(this.project_id, undefined, undefined, undefined)).subscribe(
      (products) => {
        this.fetchingInProgress = false

        this.clearProductsCache()
        this.addProductsToCache(products)
        this.allProductsLoaded = true

        observer.next(this.allProducts)
        observer.complete()
      },
      (error) => {
        this.fetchingInProgress = false
        observer.error(error)
      }
    )
  }

  private migrateProductImages(products: any[], chunkSize: number = 20): void {
    const productsToMigrate = products.filter((product) => {
      const imageUrl = product?.imageUrl
      if (!imageUrl || !imageUrl[0]) return false
      if (Array.isArray(imageUrl)) return imageUrl.some((url) => this.isValidImage(url))
      return this.isValidImage(imageUrl)
    })
    this.processProductChunk(productsToMigrate, 0, chunkSize)
  }

  private processProductChunk(products: any[], startIndex: number, chunkSize: number): void {
    if (startIndex >= products.length) return

    const chunk = products.slice(startIndex, startIndex + chunkSize)
    let completedInChunk = 0

    const onProductComplete = () => {
      completedInChunk++
      if (completedInChunk >= chunk.length) {
        this.processProductChunk(products, startIndex + chunkSize, chunkSize)
      }
    }

    chunk.forEach((product) => {
      const imageUrl = product?.imageUrl
      if (Array.isArray(imageUrl)) {
        this.migrateImageArray(product, imageUrl, onProductComplete)
      } else if (this.isValidImage(imageUrl)) {
        this.uploadImageFromUrl(imageUrl).subscribe(
          (supabaseUrl) => {
            product.imageUrl = [supabaseUrl]
            this.productsApi.updateProductImages(String(this.project_id), product.Id, product.imageUrl) .catch(err => console.warn('[ProductsService] updateProductImages failed:', err)).then(onProductComplete, onProductComplete)
          },
          () => onProductComplete()
        )
      } else {
        onProductComplete()
      }
    })
  }

  private migrateImageArray(product: any, imageUrls: string[], onComplete?: () => void): void {
    const urlsToMigrate = imageUrls.filter((url) => this.isValidImage(url))

    if (urlsToMigrate.length === 0) {
      onComplete?.()
      return
    }

    let pendingUploads = urlsToMigrate.length
    let hasChanges = false

    imageUrls.forEach((url, index) => {
      if (this.isValidImage(url)) {
        this.uploadImageFromUrl(url).subscribe(
          (supabaseUrl) => {
            product.imageUrl[index] = supabaseUrl
            hasChanges = true
            pendingUploads--
            if (pendingUploads === 0) {
              if (hasChanges) {
                this.productsApi.updateProductImages(String(this.project_id), product.Id, product.imageUrl) .catch(err => console.warn('[ProductsService] updateProductImages failed:', err)).then(onComplete, onComplete)
              } else {
                onComplete?.()
              }
            }
          },
          () => {
            pendingUploads--
            if (pendingUploads === 0) {
              if (hasChanges) {
                this.productsApi.updateProductImages(String(this.project_id), product.Id, product.imageUrl) .catch(err => console.warn('[ProductsService] updateProductImages failed:', err)).then(onComplete, onComplete)
              } else {
                onComplete?.()
              }
            }
          }
        )
      }
    })
  }

  /**
   * Downloads an image from a URL and uploads it to Firebase Storage.
   * Uses proxy to avoid CORS issues.
   */
  private uploadImageFromUrl(imageUrl: string): Observable<string> {
    return new Observable((observer) => {
      const isZoho = this.helpersService.isZohoImage(imageUrl)
      const proxyUrl = `${environment.proxyUrl}${encodeURIComponent(imageUrl)}`
      const fetchOptions: RequestInit = isZoho
        ? {
            headers: {
              Authorization: `Zoho-oauthtoken ${this.integrationsService.integrationsData.zohoAccessToken}`
            }
          }
        : {}

      fetch(proxyUrl, fetchOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`)
          }
          return response.blob()
        })
        .then((blob) => {
          const fileName = this.extractFileNameFromUrl(imageUrl)
          const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' })

          this.builderService.uploadImage(file, this.project_id, 'products').subscribe(
            (firebaseUrl) => {
              observer.next(firebaseUrl)
              observer.complete()
            },
            (error) => {
              observer.error(error)
            }
          )
        })
        .catch((error) => {
          observer.error(error)
        })
    })
  }

  /**
   * Extracts a file name from a URL, with fallback to a generated name.
   */
  private extractFileNameFromUrl = (url: string): string => {
    const validImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const segments = pathname.split('/')
      const lastSegment = segments[segments.length - 1]

      if (lastSegment) {
        const lowerSegment = lastSegment.toLowerCase()
        const hasValidExtension = validImageExtensions.some((ext) => lowerSegment.endsWith(ext))
        if (hasValidExtension) {
          return lastSegment
        }
      }
    } catch {
      // URL parsing failed
    }

    return `product-image-${Date.now()}.jpg`
  }

  isValidImage(imageUrl: any): boolean {
    return typeof imageUrl === 'string' && imageUrl !== '' &&
      imageUrl !== 'assets/images/empty-image.png' &&
      !imageUrl.includes('supabase.co/storage') &&
      !imageUrl.includes('files.instashop.com')
  }

  getVendorProducts = (projectId: any, refresh: boolean = false) => {
    return new Observable<any>((observer) => {
      if (!this.fetchingInProgress) {
        this.fetchingInProgress = true
        if (this.storedVendorProducts && this.storedVendorProducts.length > 0 && !refresh) {
          this.fetchingInProgress = false
          observer.next(this.storedVendorProducts) // Notify the observer that the operation is complete
          observer.complete() // Complete the Observable
        } else {
          from(this.productsApi.getProducts(projectId, undefined, undefined, undefined)).subscribe(
            (products) => {
              this.fetchingInProgress = false
              this.storedVendorProducts = products
              observer.next(products) // Notify the observer that the operation is complete
              observer.complete() // Complete the Observable
            },
            (error) => {
              this.fetchingInProgress = false
              observer.error(error) // Notify the observer of an error
            }
          )
        }
      }
    })
  }

  removeProduct = (product_id: string) => {
    return new Observable<any>((observer) => {
      from(this.productsApi.deleteProduct(this.project_id, product_id)).subscribe(() => {
        this.productsCache.delete(product_id)
        this.totalProductsCount = Math.max(0, this.totalProductsCount - 1)
        this.paginationCount = Math.max(0, this.paginationCount - 1)
        observer.next({ id: product_id })
        observer.complete()
      })
    })
  }

  getProduct = (product_id: string) => {
    // Always fetch from Supabase — cache may have partial categoriesIds
    // (products loaded via category filter only carry the filtered category)
    return from(this.productsApi.getProductById(this.project_id, product_id))
  }

  /**
   * Returns the subset of the given product ids that already exist in the current project.
   * @param productIds - Candidate product ids from a CSV.
   * @returns An Observable emitting the ids found in the database.
   */
  getExistingProductIds(productIds: string[]): Observable<string[]> {
    return from(this.productsApi.getExistingProductIds(this.project_id, productIds))
  }

  getProductsByCategories = (categoryArray: any, products: any) => {
    return products.filter((product: any) => {
      // Iterate through product properties (e.g., "-NemENWD4djXIoAIAj6g")
      for (const productId in product) {
        if (product.hasOwnProperty(productId) && product.categoriesIds) {
          const productCategoriesIds = Object.values(product.categoriesIds);
          // Check if there is an intersection between categoryArray and productCategoriesIds
          if (categoryArray && categoryArray.some((category: any) => this.helpersService.isArrayOfIntegers(productCategoriesIds) ? productCategoriesIds.includes(parseInt(category)) : productCategoriesIds.includes(category) )) {
            return true // Product has at least one matching category
          }
        }
      }
      return false // No matching categories for this product
    })
  }
  getProductsBySubCategories = (categoryArray: any, products: any) => {
    return products.filter((product: any) => {
      // Iterate through product properties (e.g., "-NemENWD4djXIoAIAj6g")
      for (const productId in product) {
        if (product.hasOwnProperty(productId) && product.subCategoriesIds) {
          const subCategoriesIds = Object.values(product.subCategoriesIds)
          // Check if there is an intersection between categoryArray and productCategoriesIds
          if (categoryArray && categoryArray.some((category: any) => this.helpersService.isArrayOfIntegers(subCategoriesIds) ? subCategoriesIds.includes(parseInt(category)) : subCategoriesIds.includes(category) )) {
            return true // Product has at least one matching category
          }
        }
      }
      return false // No matching categories for this product
    })
  }

  /**
   * Fetches products with pagination from Firebase
   * @param limit - Number of products to fetch per page
   * @param startAfterKey - Optional key to start fetching after (for pagination)
   * @returns Observable with items and lastKey for next page
   */
  getProductsPaginated = (limit: number, startAfterKey?: string): Observable<{ items: any[]; lastKey: string | null }> => {
    const page = startAfterKey ? parseInt(startAfterKey, 10) || 0 : 0
    return from(this.productsApi.getProducts(this.project_id, page, limit, undefined)).pipe(
      map((items) => ({
        items: items || [],
        lastKey: (items && items.length === limit) ? String(page + 1) : null
      }))
    )
  }

  /**
   * Fetches products from Firebase in paginated batches with category/sub-category filters.
   * Keeps fetching until the requested limit is fulfilled or no more data is available.
   * @param args - Configuration object with limit, query type, and category filters
   * @param pageSize - Number of products fetched per pagination request (default = 20)
   * @returns Observable emitting an array of filtered products with length <= limit
   */
  loadFilteredProductsPaginated(
    args: {
      limit: number
      query_post_type?: string
      products_categories?: any[]
      products_sub_categories?: any[]
      startAfterKey?: string
    },
    pageSize: number = 20
  ): Observable<any[]> {
    const limit = Number(args.limit)

    return this.getProductsPaginated(pageSize, args.startAfterKey).pipe(
      switchMap((response) => {
        const lastKey = response.lastKey
        const products = response.items || []

        // Add fetched products to cache
        const productsWithIds = products.map((p: any, index: number) => ({
          ...p,
          Id: p.Id || Object.keys(products)[index]
        }))
        this.addProductsToCache(productsWithIds)

        // Apply category/subcategory filters
        let filtered = this.applyFilters(productsWithIds, args)

        // If we have enough products OR no more pages, return results
        if (filtered.length >= limit || !lastKey) {
          return of(filtered.slice(0, limit))
        }

        // Otherwise, recursively fetch more products
        return this.loadFilteredProductsPaginated(
          { ...args, limit: limit - filtered.length, startAfterKey: lastKey },
          pageSize
        ).pipe(
          map((nextProducts) => [...filtered, ...nextProducts].slice(0, limit))
        )
      })
    )
  }

  /**
   * Applies category or sub-category filters to products
   * @param products - Array of products to filter
   * @param args - Filter configuration with query type and category arrays
   * @returns Filtered array of products
   */
  private applyFilters(products: any[], args: { query_post_type?: string; products_categories?: any[]; products_sub_categories?: any[] }): any[] {
    if (args.query_post_type !== 'by_category') {
      return products
    }

    if (args.products_sub_categories?.length) {
      return this.getProductsBySubCategories(args.products_sub_categories, products)
    }

    if (args.products_categories?.length) {
      return this.getProductsByCategories(args.products_categories, products)
    }

    return products
  }

  //getting product by the category id and returning an array containing the matched products
  getProductsByCatId(catId: any) {
    let productsMatching: any = []
    this.getProducts().subscribe((resp) => {
      // getting all the products and looping on them and matching the categories ids with products cat ids
      for (let i of resp) {
        if (i.categoriesIds) {
          for (let j of i.categoriesIds) {
            if (catId === j) {
              //if there is a match push in the matching array
              productsMatching.push(i)
            }
          }
        }
      }
    })
    return productsMatching
  }

  /**
   * Send the product status to the database
   */
  setProductStatus = (productId: any, hidden: boolean): Observable<any> => {
    return new Observable<any>((observer) => {
      from(this.productsApi.setProductPublish(this.project_id, productId, hidden)).subscribe(() => {
        this.updateProductInCache(productId, { publish: hidden })
        observer.next({ id: productId })
        observer.complete()
      })
    })
  }

  findProductsBySKU = (parsedCSVData: any): Observable<any> => {

    return new Observable<any>((observer) => {
      this.getProducts().subscribe(
        (products) => {
          let foundProducts: any[] = [];
    
          for (let index = 0; index < parsedCSVData.length; index++) {
            const element = parsedCSVData[index];
    
            const matches = products.filter(
              (product: any) =>
                product.productSKU.toString() === element.productSku.toString()
            );
    
            foundProducts.push(...matches); // Use spread to flatten
          }
    
          // Deduplicate by productSKU
          const uniqueProducts = Array.from(
            new Map(
              foundProducts.map((p: { productSKU: string | number }) => [
                p.productSKU,
                p,
              ])
            ).values()
          );
    
          observer.next(uniqueProducts);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        }
      );
    })
  }

  findProductsByVariableSKU = (parsedCSVData: any): Observable<any> => {

    return new Observable<any>((observer) => {
      this.getProducts().subscribe(
        (products) => {
          let foundProducts: any[] = [];

          for (let index = 0; index < parsedCSVData.length; index++) {
            const element = parsedCSVData[index];
            if(!element.variation_sku) {
              continue; // Skip if variation_sku is not present
            }
            const matchedProduct = products.find((product: any) =>
              product.productVariations?.some(
                (variation: any) =>
                  variation.sku?.toString() === element.variation_sku.toString()
              )
            );
    
            if (matchedProduct) {
              foundProducts.push(matchedProduct);
            }
          }
          // Deduplicate products by productID or productSKU
          const uniqueProducts = Array.from(
            new Map(
              foundProducts.map((p: { productSKU: string | number }) => [
                p.productSKU,
                p,
              ])
            ).values()
          );
    
          observer.next(uniqueProducts);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        }
      );
    })
  }

  submitCodeToProduct = (productId: string, codes: any, variable: boolean): Observable<any> => {
    return new Observable<any>((observer) => {
      this.getProduct(productId).subscribe((product) => {
        if (!product.codes) {
          product.codes = []
        }
        if( !variable ) {
          product.productType = 'simple'
          product.codes = codes
        } else {
          product.productType = 'variable'
          // Loop over codes from CSV
          for (const code of codes) {
            const variation = product.productVariations.find(
              (v: any) => v.sku?.toString() === code.variation_sku?.toString()
            )
            if (variation) {
              if (!variation.codes) variation.codes = []
              variation.codes.push(code)
            }
          }
        }
        this.updateProductWithFallback(this.project_id, productId, product).then(() => {
          observer.next({ id: productId })
          observer.complete()
        }).catch((error) => observer.error(error))
      }, (error) => observer.error(error))
    })
  }

  getStandaloneProducts() {
    this.getProducts('all').subscribe((response: any) => {
      const products = response.items || response
      this.storedStandaloneProducts = products
    })
  }
}
